import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  // ✅ Track close callback generation to cancel stale ones
  #closeCallbackId = 0;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;
    this.#close(this.#getDetailsElement(event));
  };

  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;
    return event.target.closest('details') ?? this.refs.details;
  }

  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  close(event) {
    const details = this.#getDetailsElement(event);
    this.#close(details);
  }

open(target, event) {
  const details = this.#getDetailsElement(event);
  const summary = details.querySelector('summary');

  if (!summary) return;

  this.#closeCallbackId++;

  summary.setAttribute('aria-expanded', 'true');

  const closeBtn = this.refs.menuDrawer.querySelector('.menu-drawer__close-button');
  closeBtn?.classList.remove('hidden');

  this.preventInitialAccordionAnimations(details);
  requestAnimationFrame(() => {
    details.classList.add('menu-open');

    if (target) {
      this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
    }

    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
    onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });

    // Dispatch menu open event
    document.dispatchEvent(new CustomEvent('header-drawer:open'));
  });
}

#close(details) {
  const summary = details.querySelector('summary');
  if (!summary) return;

  const callbackId = ++this.#closeCallbackId;

  summary.setAttribute('aria-expanded', 'false');
  details.classList.remove('menu-open');
  this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

  const closeBtn = this.refs.menuDrawer.querySelector('.menu-drawer__close-button');
  closeBtn?.classList.add('hidden');

  const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

  // Dispatch close event
  document.dispatchEvent(new CustomEvent('header-drawer:close'));

  setTimeout(() => {
    if (callbackId !== this.#closeCallbackId) return;
    details.classList.remove('menu-open');
    reset(details);
    removeTrapFocus();
  }, 500);

  onAnimationEnd(
    drawer || details,
    () => {
      if (callbackId !== this.#closeCallbackId) return;
      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
      } else {
        trapFocus(this.refs.details);
      }
    },
    { subtree: false }
  );
}

  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');
    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}